/**
 * Redondea un número al entero más cercano si se encuentra dentro de un rango de tolerancia.
 * Si está fuera de ese rango, mantiene el valor original (redondeado a dos decimales).
 *
 * Ejemplo:
 *   4.02 → 4.00   (dentro de la tolerancia)
 *   3.97 → 4.00   (dentro de la tolerancia)
 *   4.06 → 4.06   (fuera de la tolerancia)
 *
 * @param {number} num - Número a evaluar y redondear.
 * @param {number} [tolerance=0.08] - Diferencia máxima permitida para redondear al entero más cercano.
 */
export function roundWithTolerance(num, tolerance = 0.08) {
  const nearest = Math.round(num); // Calcula el entero más cercano
  const diff = Math.abs(num - nearest); // Calcula la diferencia entre el número y ese entero

  if (diff <= tolerance) {
    // Si la diferencia está dentro de la tolerancia, redondea al entero más cercano
    return Number(nearest.toFixed(2));
  } else {
    // Si está fuera de la tolerancia, conserva el valor original con dos decimales
    return Number(num.toFixed(2));
  }
}

/**
 * Redondea un número a la cantidad de decimales especificada.
 * 
 * - Si `num` no es un número válido, devuelve 0.
 * - Si `decimals` no es válido o es negativo, se asume 0.
 * - Utiliza `Number.EPSILON` para evitar errores de precisión con números flotantes.
 * 
 * @param {number} num - Número a redondear.
 * @param {number} decimals - Cantidad de decimales a mantener.
 * @returns {number} - Número redondeado con la cantidad de decimales indicada, o 0 si el valor no es válido.
 * 
 * @example
 * roundDecimals(4.125, 2); // → 4.13
 * roundDecimals('abc', 2); // → 0
 * roundDecimals(3.14159, 3); // → 3.142
 */
export function roundDecimals(num, decimals = 2) {
  if (typeof num !== 'number' || isNaN(num)) return 0;
  if (typeof decimals !== 'number' || decimals < 0) decimals = 0;

  const factor = Math.pow(10, decimals);
  return Math.round((num + Number.EPSILON) * factor) / factor;
}

/**
 * Formatea un número según un conjunto de reglas de formato:
 * - Separador de miles
 * - Separador decimal
 * - Cantidad de decimales
 * - Símbolo de moneda opcional (como prefijo o sufijo)
 *
 * Si el valor no es numérico, devuelve una cadena vacía.
 * Si `decimals` es 0, el resultado no incluirá parte decimal.
 *
 * Ejemplo:
 *   formatNumber(1234567.89, ".", ",", 2, "$", "prefix"); // → "$1,234,567.89"
 *   formatNumber(1234567.89, ",", ".", 2, "₲", "suffix"); // → "1.234.567,89₲"
 *   formatNumber(1234.5, ",", ".", 0);                    // → "1.235"
 *
 * @param {number|string} value - Número a formatear. Se intentará convertirlo a numérico.
 * @param {string} decimalSeparator - Caracter usado como separador decimal.
 * @param {string} thousandSeparator - Caracter usado como separador de miles.
 * @param {number} [decimals=2] - Cantidad de decimales a mostrar.
 * @param {string} [currencySymbol] - Símbolo de moneda opcional (ej: "$", "₲", "L").
 * @param {"prefix"|"suffix"} [currencyPosition="prefix"] - Posición del símbolo de moneda.
 * @returns {string} Número formateado según los parámetros o cadena vacía si el valor no es válido.
 */
export function formatNumber(
  value,
  decimalSeparator = ".",
  thousandSeparator = ",",
  decimals = 2,
  currencySymbol,
  currencyPosition = "prefix"
) {
  if (value === null || value === undefined || value === "") return "";

  const number = Number(value);
  if (isNaN(number)) return "";

  // Redondea el número a la cantidad de decimales solicitada
  let [integerPart, decimalPart] = number.toFixed(decimals).split(".");

  // Aplica separador de miles
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);

  let formatted = decimals === 0
    ? integerPart
    : `${integerPart}${decimalSeparator}${decimalPart}`;

  // Agrega símbolo de moneda solo si se proporciona
  if (currencySymbol) {
    formatted =
      currencyPosition === "suffix"
        ? `${formatted} ${currencySymbol}`
        : `${currencySymbol} ${formatted}`;
  }

  return formatted;
}

/**
 * Formatea un número de acuerdo con las reglas de formato del país:
 * - Separador decimal
 * - Separador de miles
 * - Cantidad de decimales
 * - Símbolo de moneda opcional
 *
 * Si el valor no es numérico, devuelve una cadena vacía.
 * Si `decimals` es 0, el resultado no incluirá parte decimal.
 *
 * Ejemplo:
 *   formatNumberByCountry(1234567.89, "EC", 2, true); // → "$1,234,567.89"
 *   formatNumberByCountry(1234567.89, "PY", 2, true); // → "₲1.234.567,89"
 *   formatNumberByCountry(1234567.89, "HN", 0, false); // → "1,234,568"
 *
 * @param {number|string} value - Número a formatear.
 * @param {string} countryCode - Código de país (ej: "EC", "PY", "HN").
 * @param {number} [decimals] - Cantidad de decimales a mostrar. Si no especifica toma configuración de decimales de countries.js
 * @param {boolean} [includeCurrency=false] - Si debe incluir el símbolo de moneda.
 * @param {"prefix"|"suffix"} [currencyPosition="prefix"] - Posición opcional del símbolo de moneda.
 * @returns {string} Número formateado según el país o cadena vacía si los datos son inválidos.
 */
export function formatNumberByCountry(
  value,
  country,
  includeCurrency = false,
  decimals = 2,
) {
  if (!country) return "";

  const decimalSeparator = country.decimalSeparator;
  const thousandSeparator = country.thousandSeparator;
  const currencySymbol = includeCurrency ? country.currencySymbol : undefined;
  const currencyPosition = country.currencyPosition;
  const quantityDecimal =  decimals || country.decimals;

  return formatNumber(
    value,
    decimalSeparator,
    thousandSeparator,
    quantityDecimal,
    currencySymbol,
    currencyPosition
  );
}

/**
 * Convierte un número formateado según reglas locales a un número válido en JavaScript.
 *
 * Ejemplo:
 *   parseNumberByCountry("100.000.200", PY) → 100000200
 *   parseNumberByCountry("300,234.66", EC) → 300234.66
 *
 * @param {string} value - Texto formateado.
 * @param {object} country - Objeto que incluye decimalSeparator, thousandSeparator, currencySymbol.
 * @returns {number|string} Número como float o "" si no es válido.
 */
export function parseNumberByCountry(value, country) {
  if (!value || typeof value !== "string") return "";

  const decimalSeparator = country.decimalSeparator; // "," o "."
  const thousandSeparator = country.thousandSeparator; // "." o ","
  const currencySymbol = country.currencySymbol || "";

  // Quitar espacios y símbolo de moneda
  let cleaned = value
    .replace(new RegExp(`\\s|${currencySymbol}`, "g"), "")
    .trim();

  // Eliminar separador de miles
  if (thousandSeparator) {
    const thousandsRegex = new RegExp(`\\${thousandSeparator}`, "g");
    cleaned = cleaned.replace(thousandsRegex, "");
  }

  // Convertir separador decimal local al estándar "."
  if (decimalSeparator && decimalSeparator !== ".") {
    const decimalRegex = new RegExp(`\\${decimalSeparator}`, "g");
    cleaned = cleaned.replace(decimalRegex, ".");
  }

  // Convertir a número
  const result = Number(cleaned);

  return isNaN(result) ? "" : result;
}

